import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { sperre } from "@/hilfe/Knopfhilfe";
import { Hilfe } from "@/hilfe/Hilfe";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, Building2, Pencil, Tag } from "lucide-react";
import { listProperties, listEntities, listUnits, listTenants, listWiki, listLiegenschaften, createProperty, updateProperty, deleteProperty } from "@/lib/api";
import { ASSET_TYPES, euro, germanDate } from "@/lib/format";
import { buildLageMap } from "@/lib/propertyLabels";
import { useSchreibschutz } from "@/lib/auth";
import { toast } from "sonner";
import DeleteConfirm from "@/components/DeleteConfirm";
import { useTableSort, SortHeader, sortOptions } from "@/components/SortHeader";
import { SearchBox, useFulltextFilter, MatchCount } from "@/components/SearchBox";
import SuchAuswahl from "@/components/ui/such-auswahl";

const empty = {
    entity_id: "", label: "", address: "", city: "", plz: "",
    asset_typ: "MFH", year_built: "", fuel_type: "",
    energieausweis_link: "", versicherung_gesellschaft: "", versicherung_police: "",
    weg_verwaltung: "", grundbuch_eintrag: "", iban: "",
    besitz_ab: "", besitz_bis: "", umschreibung_ab: "", umschreibung_bis: "",
    liegenschaft_id: "", drive_ordner_id: "",
    bestandsfotos_link: "", handbuch_link: "", notes: "",
};

const Properties = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const entityFilter = searchParams.get("entity_id") || "";
    const [items, setItems] = useState([]);
    const [entities, setEntities] = useState([]);
    const [liegenschaften, setLiegenschaften] = useState([]);
    const [units, setUnits] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [wikiEntries, setWikiEntries] = useState([]);
    const [open, setOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);
    const [assetFilter, setAssetFilter] = useState("");
    const { gesperrt, grund } = useSchreibschutz();

    const load = useCallback(async () => {
        const params = {};
        if (entityFilter) params.entity_id = entityFilter;
        if (assetFilter) params.asset_typ = assetFilter;
        const [list, es, us, ts, wk] = await Promise.all([
            listProperties(params), listEntities(), listUnits(), listTenants(), listWiki({ scope: "property" }),
        ]);
        setItems(list);
        setEntities(es);
        setUnits(us);
        setTenants(ts);
        setWikiEntries(wk);
    }, [entityFilter, assetFilter]);

    useEffect(() => { load(); }, [load]);
    // Getrennt vom Haupt-Load: die Liegenschaften braucht nur das Formular.
    // Faellt die Abfrage aus, bleibt das Auswahlfeld leer -- die Liste der
    // Immobilien darf daran nicht scheitern.
    useEffect(() => {
        listLiegenschaften().then(setLiegenschaften).catch(() => setLiegenschaften([]));
    }, []);

    const entityMap = useMemo(() => Object.fromEntries(entities.map((e) => [e.id, e])), [entities]);
    const lageMap = useMemo(() => buildLageMap(items, units), [items, units]);
    // Langform nur für die Suche: angezeigt wird "EG L", gefunden werden soll
    // es auch mit "EG links".
    const lageSearchMap = useMemo(() => buildLageMap(items, units, { short: false }), [items, units]);
    const unitsByProp = useMemo(() => {
        const m = {};
        units.forEach((u) => { (m[u.property_id] = m[u.property_id] || []).push(u); });
        return m;
    }, [units]);
    const rentByProp = useMemo(() => {
        const unitProp = Object.fromEntries(units.map((u) => [u.id, u.property_id]));
        const m = {};
        tenants.filter((t) => t.status === "aktiv" || t.status === "gekuendigt").forEach((t) => {
            const pid = unitProp[t.unit_id];
            if (!pid) return;
            m[pid] = (m[pid] || 0) + (t.kaltmiete ?? t.pauschalmiete ?? 0);
        });
        return m;
    }, [units, tenants]);
    // Verkauft-Erkennung: Wiki-Eintrag der Kategorie "Verkauf & Notariat" am Objekt
    const soldMap = useMemo(() => {
        const m = {};
        wikiEntries.forEach((e) => {
            if (e.category !== "verkauf-notariat" || !e.scope_id) return;
            if (!m[e.scope_id] || (e.created_at || "") > m[e.scope_id]) m[e.scope_id] = e.created_at;
        });
        return m;
    }, [wikiEntries]);
    const sortedEntities = useMemo(() => sortOptions(entities, (e) => e.name || ""), [entities]);
    const sortedAssetTypes = useMemo(() => sortOptions(ASSET_TYPES), []);

    const sortResolvers = useMemo(() => ({
        label: (p) => ((p.label || p.address || "") + " " + (lageMap[p.id] || "")).toLowerCase(),
        entity: (p) => (entityMap[p.entity_id]?.name || "").toLowerCase(),
        asset_typ: (p) => p.asset_typ || "",
        city: (p) => (p.city || "").toLowerCase(),
        einheiten: (p) => (unitsByProp[p.id] || []).length,
        miete: (p) => rentByProp[p.id] ?? 0,
    }), [entityMap, lageMap, unitsByProp, rentByProp]);

    // Volltextsuche
    const searchFields = useMemo(() => [
        "label", "address", "plz", "city", "asset_typ", "fuel_type",
        "grundbuch_eintrag", "besitz_ab", "liegenschaft_id", "drive_ordner_id", "notes",
        (p) => entityMap[p.entity_id]?.name || "",
        (p) => lageMap[p.id] || "",
        (p) => lageSearchMap[p.id] || "",
        (p) => (soldMap[p.id] ? "verkauft" : ""),
    ], [entityMap, lageMap, lageSearchMap, soldMap]);
    const { query, setQuery, filtered: searched, total, matches, active } = useFulltextFilter(items, searchFields);

    const { sortKey, sortDir, toggle, sorted } = useTableSort(searched, "label", "asc", sortResolvers);

    const startCreate = () => { setEditingId(null); setForm(empty); setOpen(true); };
    const startEdit = (p) => { setEditingId(p.id); setForm({ ...empty, ...p, year_built: p.year_built ?? "" }); setOpen(true); };

    const save = async () => {
        if (!form.entity_id) { toast.error("Entität auswählen"); return; }
        if (!form.address.trim()) { toast.error("Adresse ist erforderlich"); return; }
        setSaving(true);
        try {
            const payload = {
                ...form,
                year_built: form.year_built ? parseInt(form.year_built) : null,
            };
            if (editingId) {
                await updateProperty(editingId, payload);
                toast.success("Immobilie aktualisiert");
            } else {
                await createProperty(payload);
                toast.success("Immobilie angelegt");
            }
            setOpen(false); setForm(empty); setEditingId(null); load();
        } finally { setSaving(false); }
    };

    return (
        <div className="px-6 md:px-10 py-8 space-y-6">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-2">Bestand</div>
                    <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="properties-title">Immobilien</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Wirtschaftseinheiten (Häuser, WEG-Einzelobjekte, Garagenhöfe) – jede Adresse ist eine eigene Wirtschaftseinheit.
                    </p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={startCreate} data-testid="btn-new-property"
                                disabled={gesperrt} grund={grund}>
                            <Plus size={14} className="mr-1.5" /> Neue Immobilie
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader><DialogTitle>{editingId ? "Immobilie bearbeiten" : "Neue Immobilie anlegen"}</DialogTitle></DialogHeader>
                        <div className="grid grid-cols-2 gap-3 py-2">
                            <div className="col-span-2">
                                <Label>Entität *<Hilfe id="erwerbseinheit" /></Label>
                                <SuchAuswahl wert={form.entity_id} onWaehlen={(v) => setForm({ ...form, entity_id: v })}
                                    optionen={[
                                        ...entities.map((e) => ({ wert: e.id, text: e.name })),
                                    ]}
                                    platzhalter="— Eigentümer wählen —"
                                    testId="input-prop-entity"
                                />
                            </div>
                            <div>
                                <Label>Bezeichnung</Label>
                                <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="z.B. Goethestraße 12" />
                            </div>
                            <div>
                                <Label>Asset-Typ</Label>
                                <SuchAuswahl wert={form.asset_typ} onWaehlen={(v) => setForm({ ...form, asset_typ: v })}
                                    optionen={[
                                        ...ASSET_TYPES.map((a) => ({ wert: a.value, text: a.label })),
                                    ]}
                                    testId="input-prop-asset"
                                />
                            </div>
                            <div className="col-span-2">
                                <Label>Adresse *<Hilfe id="adresse-gruppierung" /></Label>
                                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="input-prop-address" />
                            </div>
                            <div>
                                <Label>PLZ</Label>
                                <Input value={form.plz} onChange={(e) => setForm({ ...form, plz: e.target.value })} />
                            </div>
                            <div>
                                <Label>Stadt</Label>
                                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                            </div>
                            <div>
                                <Label>Baujahr</Label>
                                <Input type="number" value={form.year_built} onChange={(e) => setForm({ ...form, year_built: e.target.value })} />
                            </div>
                            <div>
                                <Label>Energieträger</Label>
                                <SuchAuswahl wert={form.fuel_type} onWaehlen={(v) => setForm({ ...form, fuel_type: v })}
                                    optionen={[
                                        { wert: "Gas", text: "Gas" },
                                        { wert: "Fernwärme", text: "Fernwärme" },
                                        { wert: "Öl", text: "Öl" },
                                        { wert: "Wärmepumpe", text: "Wärmepumpe" },
                                        { wert: "Pellets", text: "Pellets" },
                                    ]}
                                    platzhalter="Auswählen"
                                />
                            </div>
                            <div className="col-span-2">
                                <Label>Energieausweis (Link)</Label>
                                <Input value={form.energieausweis_link} onChange={(e) => setForm({ ...form, energieausweis_link: e.target.value })} />
                            </div>
                            <div>
                                <Label>Versicherung Gesellschaft</Label>
                                <Input value={form.versicherung_gesellschaft} onChange={(e) => setForm({ ...form, versicherung_gesellschaft: e.target.value })} />
                            </div>
                            <div>
                                <Label>Versicherung Police-Nr.</Label>
                                <Input value={form.versicherung_police} onChange={(e) => setForm({ ...form, versicherung_police: e.target.value })} />
                            </div>
                            {form.asset_typ === "ETW" && (
                                <div className="col-span-2">
                                    <Label>WEG-Verwaltung</Label>
                                    <Input value={form.weg_verwaltung} onChange={(e) => setForm({ ...form, weg_verwaltung: e.target.value })} placeholder="z.B. Hausverwaltung XYZ GmbH" />
                                </div>
                            )}
                            <div className="col-span-2">
                                <Label>Grundbuch-Eintrag</Label>
                                <Textarea
                                    rows={2}
                                    value={form.grundbuch_eintrag}
                                    onChange={(e) => setForm({ ...form, grundbuch_eintrag: e.target.value })}
                                    placeholder="z.B. Grundbuch von Aachen, Blatt 12345, Flur 28, Flurstück 758"
                                    data-testid="input-prop-grundbuch"
                                />
                                <div className="text-[11px] text-gray-500 mt-1">Frei formuliertes Grundbuch-Aktenzeichen — relevant für Veräußerung & Notarvorgänge.</div>
                            </div>
                            <div className="col-span-2">
                                <Label>Liegenschaft<Hilfe id="liegenschaft" /></Label>
                                <SuchAuswahl wert={form.liegenschaft_id || "__keine__"} onWaehlen={(v) => setForm({ ...form, liegenschaft_id: v === "__keine__" ? "" : v })}
                                    optionen={[
                                        { wert: "__keine__", text: "— keine —" },
                                        ...liegenschaften.map((lg) => ({ wert: lg.id, text: lg.bezeichnung })),
                                    ]}
                                    platzhalter="Keiner Liegenschaft zugeordnet"
                                    testId="input-prop-liegenschaft"
                                />
                                <div className="text-[11px] text-gray-500 mt-1">
                                    Grundstück und Gebäude als Einheit. Dort hängen Teilungserklärung,
                                    Beschlüsse und Verwaltervertrag — mehrere Wohnungen desselben
                                    Hauses teilen sie sich.
                                </div>
                            </div>
                            {/* Vier Daten, vier Wirkungen (Festlegung 05.10.2026): die beiden
                                Besitzübergänge steuern das Soll, die beiden Umschreibungen sind
                                das Grundbuch. Die Einheit kann jedes überschreiben, weil der
                                Aufteiler wohnungsweise verkauft. */}
                            <div>
                                <Label>Eigentumsumschreibung (Kauf)<Hilfe id="eigentumsumschreibung" /></Label>
                                <Input type="date" value={form.umschreibung_ab || ""}
                                       onChange={(e) => setForm({ ...form, umschreibung_ab: e.target.value })}
                                       data-testid="input-prop-umschreibung-ab" />
                            </div>
                            <div>
                                <Label>Besitzübergang (Verkauf)<Hilfe id="besitz-bis" /></Label>
                                <Input type="date" value={form.besitz_bis || ""}
                                       onChange={(e) => setForm({ ...form, besitz_bis: e.target.value })}
                                       data-testid="input-prop-besitz-bis" />
                                <div className="text-[11px] text-gray-500 mt-1">
                                    Ab diesem Tag gehört die Miete dem Käufer — kein Soll mehr ab der
                                    nächsten Fälligkeit.
                                </div>
                            </div>
                            <div>
                                <Label>Eigentumsumschreibung (Verkauf)<Hilfe id="eigentumsumschreibung" /></Label>
                                <Input type="date" value={form.umschreibung_bis || ""}
                                       onChange={(e) => setForm({ ...form, umschreibung_bis: e.target.value })}
                                       data-testid="input-prop-umschreibung-bis" />
                            </div>
                            <div className="col-span-2">
                                <Label>Besitzübergang (Kauf) — im Bestand seit, Nutzen-Lasten-Wechsel<Hilfe id="besitz-ab" /></Label>
                                <Input
                                    type="date"
                                    value={form.besitz_ab || ""}
                                    onChange={(e) => setForm({ ...form, besitz_ab: e.target.value })}
                                    data-testid="input-prop-besitz-ab"
                                />
                                <div className="text-[11px] text-gray-500 mt-1">
                                    Der Tag, ab dem die Mieten diesem Bestand zustehen — nicht der
                                    Kaufvertrag, der liegt oft Monate früher. Solange das Feld leer
                                    ist, entsteht Soll für jeden Monat. Steht ein Datum darin, wird
                                    Soll erst ab dem Monat erzeugt, dessen Miete <b>nach</b> dem
                                    Übergang fällig wird: bei einem Übergang am 10.08. ist das der
                                    September, denn die Augustmiete war am 03.08. fällig und ging
                                    noch an den Verkäufer.
                                </div>
                            </div>
                            <div className="col-span-2">
                                <Label>Eigene IBAN für diese Immobilie (optional)<Hilfe id="iban-vererbung" /></Label>
                                <Input
                                    value={form.iban}
                                    onChange={(e) => setForm({ ...form, iban: e.target.value })}
                                    placeholder="Wenn leer: IBAN der Entität wird verwendet"
                                    data-testid="input-prop-iban"
                                />
                                <div className="text-[11px] text-gray-500 mt-1">Überschreibt die Entity-IBAN für Mieteingänge dieser Immobilie.</div>
                            </div>
                            <div className="col-span-2">
                                <Label>Bestandsfotos (Drive-Link)</Label>
                                <Input value={form.bestandsfotos_link} onChange={(e) => setForm({ ...form, bestandsfotos_link: e.target.value })} />
                            </div>
                            <div className="col-span-2">
                                <Label>Digitales Handbuch (Link)</Label>
                                <Input value={form.handbuch_link} onChange={(e) => setForm({ ...form, handbuch_link: e.target.value })} />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
                            <Button onClick={save} disabled={saving || gesperrt}
                                    grund={sperre([saving, "Wird gespeichert"], [gesperrt, grund])} className="bg-slate-900 hover:bg-slate-800" data-testid="btn-save-property">
                                {saving ? "Speichere…" : (editingId ? "Aktualisieren" : "Speichern")}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Filters */}
            <div className="bg-white border border-gray-200 rounded-md p-3 flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <Label className="text-xs text-gray-500">Entität</Label>
                    <SuchAuswahl wert={entityFilter || "all"} onWaehlen={(v) => setSearchParams(v === "all" ? {} : { entity_id: v })}
                        optionen={[
                            { wert: "all", text: "Alle Entitäten" },
                            ...sortedEntities.map((e) => ({ wert: e.id, text: e.name })),
                        ]}
                        className="w-[240px]"
                        testId="filter-entity"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <Label className="text-xs text-gray-500">Asset</Label>
                    <SuchAuswahl wert={assetFilter || "all"} onWaehlen={(v) => setAssetFilter(v === "all" ? "" : v)}
                        optionen={[
                            { wert: "all", text: "Alle Asset-Typen" },
                            ...sortedAssetTypes.map((a) => ({ wert: a.value, text: a.label })),
                        ]}
                        className="w-[220px]"
                        testId="filter-asset"
                    />
                </div>
                <div className="ml-auto flex items-center gap-3">
                    <SearchBox value={query} onChange={setQuery} placeholder="Suche Adresse, PLZ, Entität…" testid="properties-search" className="w-[260px]" />
                    <MatchCount matches={matches} total={total} label="Immobilien" />
                </div>
            </div>

            {/* overflow-x-auto, nicht overflow-hidden: die Tabelle ist breiter als der
                Bildschirm, sobald genug Spalten da sind — mit "hidden" verschwinden die
                rechten Spalten lautlos und sind auf keinem Weg erreichbar. Gemessen am
                10.09.2026 bei 1280 px: Mieter 210 px abgeschnitten, Einheiten 183 px. */}
            <div className="bg-white border border-gray-200 rounded-md overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-[11px] uppercase tracking-[0.12em] text-gray-500">
                        <tr>
                            <SortHeader label="Immobilie" sortKey="label" state={{sortKey, sortDir}} onToggle={toggle} />
                            <SortHeader label="Entität" sortKey="entity" state={{sortKey, sortDir}} onToggle={toggle} />
                            <SortHeader label="Asset" sortKey="asset_typ" state={{sortKey, sortDir}} onToggle={toggle} />
                            <SortHeader label="Stadt" sortKey="city" state={{sortKey, sortDir}} onToggle={toggle} />
                            <SortHeader label="Einheiten" sortKey="einheiten" state={{sortKey, sortDir}} onToggle={toggle} />
                            <SortHeader label="Miete/Mo." sortKey="miete" state={{sortKey, sortDir}} onToggle={toggle} />
                            <th className="text-right font-medium px-4 py-3 sticky right-0 bg-gray-50 shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.15)]"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {sorted.map((p) => {
                            const ent = entityMap[p.entity_id];
                            const pUnits = unitsByProp[p.id] || [];
                            const vermietet = pUnits.filter((u) => u.status === "vermietet").length;
                            return (
                                <tr key={p.id} className="group hover:bg-gray-50" data-testid={`property-row-${p.id}`}>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            <Building2 size={14} className="text-gray-400" />
                                            <div>
                                                <div className="font-medium text-slate-900 flex items-center gap-2">
                                                    {p.label || p.address}
                                                    {lageMap[p.id] && (
                                                        <span className="text-[10px] bg-sky-50 text-sky-800 border border-sky-200 rounded px-1.5 py-0.5 font-normal" data-testid={`lage-badge-${p.id}`}>
                                                            {lageMap[p.id]}
                                                        </span>
                                                    )}
                                                    {soldMap[p.id] && (
                                                        <span className="inline-flex items-center gap-1 text-[10px] bg-rose-50 text-rose-700 border border-rose-200 rounded-full px-2 py-0.5 font-normal" data-testid={`sold-badge-${p.id}`}>
                                                            <Tag size={9} /> verkauft · {germanDate(soldMap[p.id])}
                                                        </span>
                                                    )}
                                                </div>
                                                {p.label && <div className="text-xs text-gray-500">{p.address}</div>}
                                                {p.grundbuch_eintrag && (
                                                    <div className="text-[10px] text-slate-500 font-mono mt-0.5 truncate max-w-[420px]" title={p.grundbuch_eintrag}>
                                                        {p.grundbuch_eintrag}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-gray-700">{ent?.name || "—"}</td>
                                    <td className="px-4 py-3 text-gray-700 text-xs">{p.asset_typ}</td>
                                    <td className="px-4 py-3 text-gray-500 text-xs">{p.plz ? `${p.plz} ` : ""}{p.city || "—"}</td>
                                    <td className="px-4 py-3 text-gray-700 text-xs tabular-nums">
                                        {pUnits.length}
                                        {pUnits.length > 0 && (
                                            <span className="text-gray-400"> · {vermietet} vermietet</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-gray-700 tabular-nums text-xs">{rentByProp[p.id] ? euro(rentByProp[p.id]) : "—"}</td>
                                    <td className="px-4 py-3 text-right whitespace-nowrap sticky right-0 bg-white group-hover:bg-gray-50 shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.15)]">
                                        <Button variant="ghost" size="sm" className="h-8 mr-1" onClick={() => startEdit(p)} data-testid={`edit-property-${p.id}`}
                                                disabled={gesperrt} grund={grund}>
                                            <Pencil size={12} />
                                        </Button>
                                        <DeleteConfirm
                                            label={p.label || p.address}
                                            kind="property"
                                            onDelete={(cascade) => deleteProperty(p.id, cascade)}
                                            onDeleted={load}
                                            testid={`delete-property-${p.id}`}
                                            disabled={gesperrt}
                                            grund={grund}
                                        />
                                        <Link to={`/immobilien/${p.id}`} data-testid={`view-property-${p.id}`}>
                                            <Button variant="ghost" size="sm" className="h-8">Details</Button>
                                        </Link>
                                    </td>
                                </tr>
                            );
                        })}
                        {items.length === 0 && (
                            <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-400">Keine Immobilien gefunden.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Properties;
